import DOMPurify from "dompurify";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";

import { unwrapApiData, unwrapApiRecord } from "../../shared/utils/apiResponse";
import { formatDate, formatTimeRange } from "../../shared/utils/dateTime";
import { useUserContext } from "../../userDashboard/contexts/userContext";
import { handleGetPublicGameById } from "../../userDashboard/userControllers/gameController";
import {
    handleFollowOrganizer,
    handleGetOrganizerFollowStatus,
    handleUnfollowOrganizer,
} from "../../userDashboard/userControllers/organizerController";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface UserSummary {
    id: string | number;
    name?: string;
    username?: string;
    image?: string;
    email?: string;
}

interface LineupItem {
    user?: UserSummary;
    [key: string]: unknown;
}

interface Match {
    id: string | number;
    title: string;
    description: string;
    image?: string;
    type?: string;
    status?: string;
    host_status?: string;
    is_pro?: boolean;
    price: number;
    currency_code?: string;
    currency?: string;
    date: string;
    start_time: string;
    end_time?: string;
    players_remaining?: number;
    players_needed?: number;
    booked_count?: number;
    confirmed_players?: number;
    important_info?: string;
    policy?: string;
    venue?: string;
    organizer?: UserSummary;
    submitted_by?: UserSummary;
    users?: UserSummary[];
    lineup?: LineupItem[];
    city?: string;
    state?: string;
    country_code?: string;
    pitch?: {
        name?: string;
        location?: string;
        contact_details?: unknown;
    };
}

const decodeSharedId = (value: string | null | undefined): string | null => {
    if (!value) return null;

    const raw = value.trim();
    if (!raw) return null;

    try {
        const normalized = raw.replace(/-/g, "+").replace(/_/g, "/");
        const padding = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
        const decoded = atob(normalized + padding).trim();

        if (/^\d+$/.test(decoded)) {
            return decoded;
        }
    } catch {
        // use raw id
    }

    return raw;
};

const getInitials = (name = "") =>
    name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "PL";

const formatContactDetails = (input: unknown): string[] => {
    if (!input) return [];

    if (typeof input === "string") {
        const trimmed = input.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed) as unknown;
            return formatContactDetails(parsed);
        } catch {
            return [trimmed];
        }
    }

    if (Array.isArray(input)) {
        return input
            .map((entry) => String(entry ?? "").trim())
            .filter(Boolean);
    }

    if (typeof input === "object") {
        return Object.entries(input as Record<string, unknown>)
            .map(([key, value]) => {
                if (value === null || value === undefined || value === "") return "";

                let valueLabel: string;
                if (Array.isArray(value)) {
                    valueLabel = value.join(", ");
                } else if (typeof value === "object" && value !== null) {
                    valueLabel = JSON.stringify(value);
                } else {
                    valueLabel = String(value);
                }

                const keyLabel = key.replace(/_/g, " ").trim();
                return keyLabel ? `${keyLabel}: ${valueLabel}` : valueLabel;
            })
            .filter(Boolean);
    }

    return [String(input)];
};

const hasRichTextMarkup = (value?: string): boolean => {
    if (!value) {
        return false;
    }

    return /<\/?[a-z][\s\S]*>/i.test(value);
};

const getStatusBadgeClass = (status?: string): string => {
    const normalized = String(status || "").toLowerCase();

    if (normalized.includes("upcoming")) return "bg-blue-100 text-blue-700";
    if (normalized.includes("ongoing")) return "bg-amber-100 text-amber-700";
    if (normalized.includes("finished") || normalized.includes("completed")) return "bg-slate-100 text-slate-700";
    if (normalized.includes("cancel")) return "bg-red-100 text-red-700";

    return "bg-gray-100 text-gray-700";
};

const StatusBadge: React.FC<{
    status?: string | null | undefined,
    label: string,
    isPro?: boolean | undefined,
    hostStatus?: string | null | undefined
}> = ({ status, label, isPro, hostStatus }) => (
    <div className="flex flex-wrap items-center gap-3">
        <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide ${getStatusBadgeClass(status || undefined)}`}>
            {label}
        </span>
        {isPro && (
            <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide bg-amber-100 text-amber-800">
                Pro Game
            </span>
        )}
        {hostStatus && (
            <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide bg-emerald-100 text-emerald-700">
                Host {hostStatus}
            </span>
        )}
    </div>
);

const GameDetailItem: React.FC<{ label: string, value: string, meta?: string | null }> = ({ label, value, meta }) => (
    <div className="bg-gray-50 p-5 rounded-3xl border border-gray-100">
        <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-lg md:text-xl font-black text-gray-900">{value}</p>
        {meta && <p className="text-xs text-gray-500 mt-1">{meta}</p>}
    </div>
);

const LocationCard: React.FC<{ name: string, summary: string | null, contactLines: string[] }> = ({ name, summary, contactLines }) => (
    <div className="p-7 bg-gray-50 rounded-[32px] border border-gray-100">
        <h4 className="font-black text-gray-900 text-sm uppercase tracking-widest mb-4">Location</h4>
        <p className="text-lg font-black text-gray-900">{name}</p>
        {summary && (
            <p className="text-sm text-gray-600 mt-1">{summary}</p>
        )}
        {contactLines.length > 0 && (
            <div className="mt-5 space-y-2">
                <p className="text-xs font-black uppercase tracking-wider text-gray-500">Contact Details</p>
                {contactLines.map((line, index) => (
                    <p key={`${line}-${index}`} className="text-sm text-gray-700">{line}</p>
                ))}
            </div>
        )}
    </div>
);

const OrganizerCard: React.FC<{
    organizer?: UserSummary | undefined;
    organizerName: string;
    organizerLink: string;
    followersCount: number;
    isOwnOrganizer: boolean;
    isFollowing: boolean;
    followLoading: boolean;
    handleFollowToggle: () => void;
    userProfile: unknown;
}> = ({
    organizer,
    organizerName,
    organizerLink,
    followersCount,
    isOwnOrganizer,
    isFollowing,
    followLoading,
    handleFollowToggle,
    userProfile,
}) => (
        <div className="p-7 bg-white rounded-[32px] border border-gray-100 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-green-700 font-black mb-4">Organizer</p>
            <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-black text-lg overflow-hidden">
                    {organizer?.image ? (
                        <img
                            src={organizer.image}
                            alt={organizerName}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        organizerName.charAt(0)
                    )}
                </div>
                <div>
                    <p className="text-lg font-black text-gray-900">{organizerName}</p>
                    <p className="text-xs text-gray-400">Hosted on SACA</p>
                </div>
            </div>
            {followersCount > 0 && (
                <p className="text-xs text-gray-400 uppercase tracking-widest font-bold mb-4">
                    {followersCount} followers
                </p>
            )}
            <div className="grid grid-cols-1 gap-3">
                <Link
                    to={organizerLink}
                    className="block w-full text-center py-4 bg-gray-900 hover:bg-green-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest transition-all"
                >
                    View organizer page
                </Link>
                {userProfile ? (() => {
                    let buttonText: string;
                    if (isOwnOrganizer) {
                        buttonText = "Your profile";
                    } else if (followLoading) {
                        buttonText = "Please wait";
                    } else if (isFollowing) {
                        buttonText = "Following";
                    } else {
                        buttonText = "Follow organizer";
                    }

                    let buttonClass: string;
                    if (isOwnOrganizer) {
                        buttonClass = "bg-gray-100 text-gray-400 cursor-not-allowed";
                    } else if (isFollowing) {
                        buttonClass = "bg-white border border-gray-200 text-gray-800 hover:border-gray-300";
                    } else {
                        buttonClass = "bg-green-700 text-white hover:bg-green-800";
                    }

                    return (
                        <button
                            type="button"
                            onClick={handleFollowToggle}
                            disabled={followLoading || isOwnOrganizer}
                            className={`w-full text-center py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all ${buttonClass}`}
                        >
                            {buttonText}
                        </button>
                    );
                })() : (
                    <Link
                        to="/sign-in"
                        className="block w-full text-center py-4 bg-green-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest transition-all"
                    >
                        Sign in to follow
                    </Link>
                )}
            </div>
        </div>
    );

const AboutSection: React.FC<{
    description?: string,
    sanitizedDescription: string,
    isRichText: boolean
}> = ({ description, sanitizedDescription, isRichText }) => (
    <section className="space-y-5">
        <h2 className="text-2xl md:text-3xl font-black text-gray-900 border-l-8 border-green-700 pl-5 uppercase tracking-tight">About This Match</h2>
        <div className="prose prose-lg prose-green max-w-none">
            {description ? (
                isRichText ? (
                    <div
                        className="font-Manrope text-gray-600 leading-[1.8] text-lg md:text-xl"
                        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                    />
                ) : (
                    <p className="font-Manrope text-gray-600 leading-[1.8] text-lg md:text-xl whitespace-pre-line">
                        {description}
                    </p>
                )
            ) : (
                <p className="font-Manrope text-gray-600 leading-[1.8] text-lg md:text-xl">
                    No description provided yet.
                </p>
            )}
        </div>
    </section>
);
const DetailedMatch: React.FC = () => {
    // ... hooks ... (kept inside for now, but will use the props if needed)
    const { id: routeId } = useParams();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { userProfile } = useUserContext();
    const countryCodeParam = searchParams.get("country") || searchParams.get("country_code");
    const stateParam = searchParams.get("state");
    const cityParam = searchParams.get("city");
    const matchId = decodeSharedId(routeId) || decodeSharedId(searchParams.get("id"));
    const [match, setMatch] = useState<Match | null>(null);
    const [loading, setLoading] = useState(true);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [followLoading, setFollowLoading] = useState(false);
    const locationContext = useMemo(() => ({
        countryCode: countryCodeParam,
        state: stateParam,
        city: cityParam,
    }), [cityParam, countryCodeParam, stateParam]);

    useEffect(() => {
        const fetchMatch = async () => {
            if (!matchId) {
                setLoading(false);
                return;
            }
            try {
                const data = await handleGetPublicGameById(matchId, locationContext);
                const resolved = unwrapApiData<Match>(data) ?? (data as unknown as Match);
                setMatch(resolved);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchMatch();
    }, [locationContext, matchId]);

    useEffect(() => {
        const fetchFollowStatus = async () => {
            if (!match?.organizer?.id || !userProfile) {
                return;
            }
            try {
                const response = await handleGetOrganizerFollowStatus(String(match.organizer.id));
                const payload = unwrapApiRecord<{ is_following?: boolean; followers_count?: number }>(response);
                setIsFollowing(Boolean(payload?.is_following));
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
            } catch (error) {
                console.warn('Follow status fetch failed:', error);
            }
        };

        fetchFollowStatus();
    }, [match?.organizer?.id, userProfile]);

if (loading) return <div className="py-16"><LoadingGrid count={1} /></div>;
    if (!match) return <div className="h-screen flex items-center justify-center font-Manrope font-black text-gray-400 text-2xl italic">Match not found.</div>;

    const isDescriptionRichText = hasRichTextMarkup(match.description || "");
    const sanitizedDescription = DOMPurify.sanitize(match.description || "", {
        ADD_ATTR: ["target"],
    });
    const isPolicyRichText = hasRichTextMarkup(match.policy || "");
    const sanitizedPolicy = DOMPurify.sanitize(match.policy || "", {
        ADD_ATTR: ["target"],
    });
    const organizer = match.organizer;
    const organizerName = organizer?.name || organizer?.username || "Organizer";
    const organizerLink = organizer?.id ? `/organizers/${organizer.id}` : "";
    const currency = match.currency_code || match.currency || "NGN";
    const isOwnOrganizer = Boolean(userProfile && organizer?.id && String(userProfile.id) === String(organizer.id));
    const playersNeeded = Number(match.players_needed || 0);
    const confirmedPlayers = Number(match.confirmed_players || match.booked_count || match.lineup?.length || match.users?.length || 0);
    let slotsRemaining: number | null = null;
    if (typeof match.players_remaining === "number") {
        slotsRemaining = match.players_remaining;
    } else if (playersNeeded > 0) {
        slotsRemaining = Math.max(playersNeeded - confirmedPlayers, 0);
    }

    const slotsRemainingLabel = slotsRemaining === null ? "Spots not set" : `${slotsRemaining} spots left`;
    const progress = playersNeeded > 0 ? Math.min((confirmedPlayers / playersNeeded) * 100, 100) : 0;
    const submitter = match.submitted_by || organizer;
    let submitterName = "Admin";
    if (submitter?.name) {
        submitterName = submitter.name;
    } else if (submitter?.username) {
        submitterName = `@${submitter.username.replace(/@/g, "")}`;
    }
    const submitterMeta = [
        submitter?.username ? `@${submitter.username.replace(/^@/, "")}` : null,
        submitter?.id !== undefined && submitter?.id !== null ? `ID: ${submitter.id}` : null,
    ].filter(Boolean).join(" • ");

    const lineupUsers = (match.lineup?.length
        ? match.lineup.map((item) => item.user).filter(Boolean)
        : (match.users || [])) as UserSummary[];

    const locationSummary = [
        match.pitch?.location,
        [match.city, match.state, match.country_code].filter(Boolean).join(", "),
    ].filter(Boolean).join(" • ");
    const contactDetails = formatContactDetails(match.pitch?.contact_details);
    const statusLabel = match.status || "Scheduled";
    const normalizedStatus = String(match.status || "").trim().toLowerCase();
    const isJoinable = !["finished", "completed", "cancelled", "canceled"].includes(normalizedStatus);
    const isUserPro = userProfile?.is_pro === true || userProfile?.is_pro === 1;
    const requiresProApproval = Boolean(match.is_pro && userProfile && !isUserPro);
    const dashboardMatchPath = `/dashboard/matches/${match.id}`;
    const primaryBookingPath = requiresProApproval
        ? "/dashboard/pro"
        : userProfile
            ? dashboardMatchPath
            : `/sign-in?redirect=${encodeURIComponent(dashboardMatchPath)}`;
    const primaryBookingLabel = requiresProApproval
        ? "Apply for SACA Pro"
        : userProfile
            ? "Book / Join Now"
            : "Sign in to Book";

    const handleFollowToggle = async () => {
        if (!organizer?.id) return;
        if (!userProfile) {
            enqueueSnackbar("Please sign in to follow organizers", { variant: "info" });
            return;
        }
        if (isOwnOrganizer) {
            enqueueSnackbar("You cannot follow yourself", { variant: "warning" });
            return;
        }

        setFollowLoading(true);
        try {
            if (isFollowing) {
                const response = await handleUnfollowOrganizer(String(organizer.id));
                const payload = unwrapApiRecord<{ followers_count?: number }>(response);
                setIsFollowing(false);
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
                enqueueSnackbar("Organizer unfollowed", { variant: "success" });
            } else {
                const response = await handleFollowOrganizer(String(organizer.id));
                const payload = unwrapApiRecord<{ followers_count?: number }>(response);
                setIsFollowing(true);
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
                enqueueSnackbar("Organizer followed", { variant: "success" });
            }
        } catch (error: unknown) {
            const err = error as { message?: string };
            enqueueSnackbar(err?.message || "Unable to update follow status", { variant: "error" });
        } finally {
            setFollowLoading(false);
        }
    };

    return (
        <>
        <Navbar />
        <div className="min-h-screen bg-white font-Manrope">
            {/* Hero Image */}
            <div className="relative h-[52vh] min-h-[380px] md:min-h-[430px] w-full overflow-hidden">
                <img
                    src={match.image || landingAsset("stadia.svg")}
                    className="w-full h-full object-cover transform scale-105"
                    alt=""
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/65 to-black/35" />

                <div className="absolute bottom-8 md:bottom-12 left-4 md:left-12 right-4 md:right-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="space-y-4">
                        <span className="px-6 py-2 bg-green-700 text-white rounded-full font-black text-xs uppercase tracking-widest shadow-2xl">
                            {match.type || "Public Match"}
                        </span>
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tighter leading-tight drop-shadow-2xl">
                            {match.title}
                        </h1>
                        <StatusBadge
                                status={match.status}
                                label={statusLabel}
                                isPro={match.is_pro}
                            hostStatus={match.host_status}
                        />
                    </div>
                    <div className="bg-white/10 backdrop-blur-3xl p-6 rounded-[28px] border border-white/20 text-white min-w-[240px] md:min-w-[290px]">
                        <p className="text-sm font-black uppercase tracking-widest text-white/60 mb-2">Joining Fee</p>
                        <p className="text-4xl font-black">{currency} {Number(match.price || 0).toLocaleString()}</p>
                        {isJoinable ? (
                            <Link
                                to={primaryBookingPath}
                                className="mt-5 flex w-full items-center justify-center rounded-2xl bg-green-600 px-5 py-4 text-center text-base font-black text-white shadow-xl transition hover:bg-green-500 active:scale-95"
                            >
                                {primaryBookingLabel}
                            </Link>
                        ) : (
                            <div className="mt-5 rounded-2xl bg-white/10 px-5 py-4 text-center text-sm font-bold text-white/70">
                                Booking closed
                            </div>
                        )}
                        {match.is_pro && (
                            <p className="mt-3 text-center text-xs font-semibold text-amber-200">
                                Approved SACA Pro members only
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-14 py-12 md:py-14">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12">
                    {/* Left: Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        <section className="bg-white border border-gray-100 rounded-[40px] p-6">
                            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-6">Game Details</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <GameDetailItem
                                        label="Date"
                                        value={formatDate(match.date, {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        }, "TBA")}
                                    />
                                    <GameDetailItem
                                        label="Time"
                                        value={formatTimeRange(match.start_time, match.end_time || null, "TBA")}
                                    />
                                    <GameDetailItem
                                        label="Venue"
                                        value={match.pitch?.name || match.venue || "TBA"}
                                        meta={locationSummary}
                                    />
                                    <GameDetailItem
                                        label="Submitted By"
                                        value={submitterName}
                                        meta={submitterMeta}
                                />
                            </div>
                        </section>

                        {match.important_info && (
                            <section className="bg-amber-50 border border-amber-100 rounded-[40px] p-6">
                                <h2 className="text-2xl font-black text-amber-900 uppercase tracking-tight mb-3">Important Info</h2>
                                <p className="text-amber-800 whitespace-pre-line">{match.important_info}</p>
                            </section>
                        )}

                        <section className="bg-white border border-gray-100 rounded-[40px] p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Players Overview</h2>
                                <span className="text-sm text-gray-500 font-bold">
                                        {confirmedPlayers} / {playersNeeded || 0} confirmed
                                    </span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-3 mb-2 overflow-hidden">
                                    <div
                                        className="bg-green-700 h-full rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <div className="flex justify-between text-xs text-slate-500 font-semibold">
                                    <span>{progress.toFixed(0)}% filled</span>
                                    <span>{slotsRemainingLabel}</span>
                                </div>
                            </section>

                        <section className="bg-white border border-gray-100 rounded-[40px] overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Line-up</h2>
                            </div>
                            {lineupUsers.length === 0 ? (
                                <div className="p-6 text-center text-gray-500 font-semibold">No players have joined yet.</div>
                            ) : (
                                <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {lineupUsers.map((user) => {
                                        const displayName = user.name || user.username || "Player";
                                        return (
                                            <div key={String(user.id)} className="px-4 py-3 flex items-center gap-3 border border-gray-100 rounded-2xl bg-gray-50/60">
                                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-black text-sm overflow-hidden">
                                                    {user.image ? (
                                                        <img src={user.image} alt={displayName} className="w-full h-full object-cover" />
                                                    ) : (
                                                        getInitials(displayName)
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-gray-900">{displayName}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        <AboutSection
                            description={match.description}
                            sanitizedDescription={sanitizedDescription}
                            isRichText={isDescriptionRichText}
                        />

                        {match.policy && (
                            <section className="bg-gray-50 border border-gray-100 rounded-[40px] p-6">
                                <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-3">Match Rules</h2>
                                {isPolicyRichText ? (
                                    <div
                                        className="prose prose-sm max-w-none text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: sanitizedPolicy }}
                                    />
                                ) : (
                                    <p className="text-gray-700 whitespace-pre-line">
                                        {match.policy}
                                    </p>
                                )}
                                </section>
                        )}

                    </div>

                    {/* Right: Sidebar / CTA */}
                    <div className="space-y-6 md:space-y-8">
                        <div className="bg-gray-900 p-8 rounded-[40px] shadow-2xl relative overflow-hidden group">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-green-700/20 rounded-full group-hover:scale-150 transition-transform duration-700" />

                            <h3 className="text-2xl font-black text-white mb-5 relative z-10">Want to Play?</h3>
                            <p className="text-white/60 font-medium text-base mb-6 relative z-10 italic">
                                Join this match and compete with players in the SACA community. Secured spots are first come, first served.
                            </p>

                            <div className="space-y-4 relative z-10">
                                {isJoinable ? (
                                    <Link
                                        to={primaryBookingPath}
                                        className="block w-full text-center py-4 bg-green-700 hover:bg-green-800 text-white rounded-2xl font-black text-lg transition-all shadow-xl active:scale-95"
                                    >
                                        {primaryBookingLabel}
                                    </Link>
                                ) : (
                                    <div className="block w-full text-center py-4 bg-white/10 text-white/60 rounded-2xl font-black text-lg">
                                        Booking closed
                                    </div>
                                )}
                                {!userProfile && isJoinable && (
                                    <Link
                                        to={`/sign-up?redirect=${encodeURIComponent(dashboardMatchPath)}`}
                                        className="block w-full text-center py-4 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-black text-base backdrop-blur-lg border border-white/20 transition-all"
                                    >
                                        Create an account
                                    </Link>
                                )}
                            </div>
                        </div>

                            {organizerLink && (
                                <OrganizerCard
                                    organizer={organizer}
                                    organizerName={organizerName}
                                    organizerLink={organizerLink}
                                    followersCount={followersCount}
                                    isOwnOrganizer={isOwnOrganizer}
                                    isFollowing={isFollowing}
                                    followLoading={followLoading}
                                    handleFollowToggle={handleFollowToggle}
                                    userProfile={userProfile}
                                />
                            )}

                            <LocationCard
                                name={match.pitch?.name || match.venue || "TBA"}
                                summary={locationSummary}
                                contactLines={contactDetails}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Banner />
            <Footer />
        </>
    );
};

export default DetailedMatch;
